/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useEffect, useState } from 'react';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { Button, IconButton, Snackbar, SnackbarContent, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { REQUEST_TEAM_LABEL, REQUEST_TEAM_URL } from './requestAccess';

// Re-show throttle: a single denied click can post the same NotAllowedError
// more than once; don't reopen the snackbar for repeats inside this window.
const REOPEN_THROTTLE_MS = 5_000;

const useStyles = makeStyles(theme => ({
  content: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
  },
  action: {
    color: theme.palette.primary.main,
  },
}));

// Backstage throws NotAllowedError (name === 'NotAllowedError') specifically
// for permission-policy denials; some API clients also surface a 403. Match
// both so the prompt fires wherever a mutation is denied, but nothing broader.
const isPermissionDenied = (error: Error): boolean => {
  if (error.name === 'NotAllowedError') return true;
  const status =
    (error as { status?: number }).status ??
    (error as { statusCode?: number }).statusCode;
  return status === 403;
};

/**
 * Global permission-denied affordance (P1 / PLAT-254). Mounted once at the app
 * root: listens to the errorApi and, when a mutation is denied anywhere
 * (NotAllowedError / 403), pops a snackbar pointing the user at the request-to-
 * join flow -- the same recovery path as the Home read-only notice, but at the
 * exact moment of denial. Complements (doesn't replace) the Home banner.
 */
export const RequestAccessOnDenied = () => {
  const classes = useStyles();
  const errorApi = useApi(errorApiRef);
  const [open, setOpen] = useState(false);
  const lastShownRef = React.useRef(0);

  useEffect(() => {
    const sub = errorApi.error$().subscribe(({ error }) => {
      if (!error || !isPermissionDenied(error)) return;
      const now = Date.now();
      if (now - lastShownRef.current < REOPEN_THROTTLE_MS) return;
      lastShownRef.current = now;
      setOpen(true);
    });
    return () => sub.unsubscribe();
  }, [errorApi]);

  const close = () => setOpen(false);

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      onClose={(_e, reason) => {
        // Keep it up on click-away; only an explicit action/dismiss closes it,
        // so the recovery link doesn't vanish before the user can use it.
        if (reason !== 'clickaway') close();
      }}
    >
      <SnackbarContent
        className={classes.content}
        message="You don't have permission for that action. Write access requires membership in a team."
        action={[
          <Button
            key="request"
            size="small"
            className={classes.action}
            href={REQUEST_TEAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
          >
            Request to join {REQUEST_TEAM_LABEL}
          </Button>,
          <IconButton
            key="dismiss"
            size="small"
            aria-label="Dismiss"
            color="inherit"
            onClick={close}
          >
            <CloseIcon fontSize="small" />
          </IconButton>,
        ]}
      />
    </Snackbar>
  );
};
